import React, { useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import createNonPropagatingEventHandler from '../../../../utils/createNonPropagatingEventHandler';
import { palette, spaces } from '../../../../assets/styles/themes/tokens';
import getTextTruncationSx from '../../../../utils/getTextTruncationSx';

interface NewCardDescriptionProps {
  description?: string;
  lines?: number;
  isExpanded: boolean;
  onShowToggleClick: () => void;
  testId?: string
}

const NewCardDescription: React.FC<NewCardDescriptionProps> = ({
  description,
  lines = 2,
  isExpanded,
  onShowToggleClick: onShowMoreClick,
  testId,
}) => {
  const { t } = useTranslation();
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const descriptionRef = React.useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (!descriptionRef.current) return;
  
    const checkOverflow = () => {
      if (descriptionRef.current) {
        const newIsOverflowing = descriptionRef.current.scrollHeight > descriptionRef.current.clientHeight;
        setIsOverflowing(newIsOverflowing);
      }
    };
  
    const debounceFn = debounce(checkOverflow);
    const observer = new ResizeObserver(checkOverflow);
  
    observer.observe(descriptionRef.current);
    checkOverflow();
  
    return () => {
      observer.disconnect();
      debounceFn.cancel();
    };
  }, [isExpanded, description]);

  if (!description) return null;

  const renderDescriptionExpand = () => {
    const showLess = isExpanded;
    const showMore = isOverflowing && !isExpanded;
  
    if (!showLess && !showMore) return null;
  
    return (
      <Box component='span'>
        <Button
          sx={{
            marginLeft: `-${spaces.xs}`,
          }}
          variant='text'
          size='small'
          color='secondary'
          onClick={createNonPropagatingEventHandler(onShowMoreClick)}
        >
          {showLess ? t('Show less') : t('Show more')}
        </Button>
      </Box>
    );
  };

  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant='body2'
        ref={descriptionRef}
        sx={{
          ...(isExpanded ? { } : { ...getTextTruncationSx(lines) }),
          color: palette.text.secondary,
        }}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} >
      </Typography>
      { renderDescriptionExpand() }
    </Box>
  );
};

export default NewCardDescription;
